import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select, SelectItem, Stack, TextInput } from '@carbon/react';
import { Add, TrashCan } from '@carbon/react/icons';

/**
 * The same week keys and window shape as the server's `PublicOrderingHours`
 * (`apps/server/src/restaurants/branches/entities/restaurantBranch.entity.ts`), which Plan 05's
 * `openingHours` column reuses (spec §3). Display order is Monday-first; the JSON object's key
 * order is not significant.
 */
export const OPENING_HOURS_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export type OpeningHoursDay = (typeof OPENING_HOURS_DAYS)[number];
export type OpeningHoursWindow = { open: string; close: string };
export type OpeningHours = Partial<Record<OpeningHoursDay, OpeningHoursWindow[]>>;

/** The three states the contract distinguishes, and the Select's three option values. */
export type DayMode = 'unset' | 'closed' | 'open';

export const DAY_MODE_NOT_SET: DayMode = 'unset';
export const DAY_MODE_CLOSED: DayMode = 'closed';
export const DAY_MODE_OPEN: DayMode = 'open';

const DEFAULT_WINDOW: OpeningHoursWindow = { open: '09:00', close: '22:00' };

const DAY_LABEL_KEY: Record<OpeningHoursDay, string> = {
  mon: 'branchHours.mon',
  tue: 'branchHours.tue',
  wed: 'branchHours.wed',
  thu: 'branchHours.thu',
  fri: 'branchHours.fri',
  sat: 'branchHours.sat',
  sun: 'branchHours.sun',
};

/**
 * Which of the three states a stored day is in. `undefined` and `[]` are different claims
 * (spec §3): a day that is absent says nothing and is not rendered, a day present with no
 * windows says "closed".
 */
export function dayMode(hours: OpeningHours | null | undefined, day: OpeningHoursDay): DayMode {
  const windows = hours?.[day];
  if (!Array.isArray(windows)) {
    return DAY_MODE_NOT_SET;
  }
  return windows.length === 0 ? DAY_MODE_CLOSED : DAY_MODE_OPEN;
}

/** Writes one day back, leaving the others alone. "Not set" removes the key entirely. */
export function setDayMode(hours: OpeningHours, day: OpeningHoursDay, mode: DayMode): OpeningHours {
  const next: OpeningHours = { ...hours };
  if (mode === DAY_MODE_NOT_SET) {
    delete next[day];
    return next;
  }
  if (mode === DAY_MODE_CLOSED) {
    next[day] = [];
    return next;
  }
  next[day] = next[day]?.length ? next[day] : [{ ...DEFAULT_WINDOW }];
  return next;
}

export function setWindow(
  hours: OpeningHours,
  day: OpeningHoursDay,
  index: number,
  patch: Partial<OpeningHoursWindow>
): OpeningHours {
  const windows = hours[day] ?? [];
  return {
    ...hours,
    [day]: windows.map((window, position) => (position === index ? { ...window, ...patch } : window)),
  };
}

export function addWindow(hours: OpeningHours, day: OpeningHoursDay): OpeningHours {
  return { ...hours, [day]: [...(hours[day] ?? []), { ...DEFAULT_WINDOW }] };
}

/** Removing a day's last window leaves `[]`, which is the same claim as choosing "Closed". */
export function removeWindow(hours: OpeningHours, day: OpeningHoursDay, index: number): OpeningHours {
  return { ...hours, [day]: (hours[day] ?? []).filter((_window, position) => position !== index) };
}

/**
 * The payload. Always an object, never `undefined`: the branch update merges the body onto the
 * stored row, so an omitted `openingHours` would leave the stored hours in place — clearing the
 * table has to send `{}` to actually clear it, and `{}` is "not configured" by the spec's rule.
 *
 * A window with a blank time is not a window. A day left with none is dropped rather than written
 * as `[]`: "closed" is a claim, and an owner who left a time field empty did not make it.
 */
export function toOpeningHoursPayload(hours: OpeningHours | null | undefined): OpeningHours {
  const payload: OpeningHours = {};
  for (const day of OPENING_HOURS_DAYS) {
    const windows = hours?.[day];
    if (!Array.isArray(windows)) {
      continue;
    }
    if (windows.length === 0) {
      payload[day] = [];
      continue;
    }
    // A hand-edited row can hold anything in a day's list; a non-window element is dropped here
    // rather than dereferenced, so the payload builder tolerates it. The render path is a known,
    // parked hazard for data no API can produce: it still dereferences every element, so a `null`
    // entry in a stored day throws while the shared branch form renders.
    const complete = windows.filter(
      (window) =>
        typeof window?.open === 'string' &&
        typeof window?.close === 'string' &&
        window.open.trim() !== '' &&
        window.close.trim() !== ''
    );
    if (complete.length > 0) {
      payload[day] = complete;
    }
  }
  return payload;
}

interface OpeningHoursFieldProps {
  value: OpeningHours;
  onChange: (next: OpeningHours) => void;
}

/**
 * The opening-hours table, and — since the storefront's Ordering screen reuses it — the ordering
 * window too. The two columns have the same shape but are different claims: `openingHours` says
 * "we are open", `publicOrderingHours` says "orders may be placed", and only the second is read
 * by the public ordering API. This component renders whichever it is handed; the BRANCH FORM
 * still passes only `openingHours`, which is what keeps that form unable to touch the ordering
 * window.
 *
 * The times are `type="time"` inputs rather than Carbon's `TimePicker`: the browser then
 * guarantees the server's "HH:MM" contract, which is the same reasoning that made the retired
 * colour field a plain validated text input.
 */
const OpeningHoursField = React.memo(({ value, onChange }: OpeningHoursFieldProps) => {
  const { t } = useTranslation();

  return (
    <fieldset className={'branch-hours'}>
      <legend className={'cds--label'}>{t('branchHours.title')}</legend>
      <p className={'cds--form__helper-text'}>{t('branchHours.hint')}</p>
      <Stack gap={5}>
        {OPENING_HOURS_DAYS.map((day) => {
          const mode = dayMode(value, day);
          const windows = value[day] ?? [];
          return (
            <div key={day} className={'branch-hours__day'}>
              <Select
                id={`opening-hours-${day}`}
                labelText={t(DAY_LABEL_KEY[day])}
                value={mode}
                onChange={(event) => onChange(setDayMode(value, day, event.target.value as DayMode))}
              >
                <SelectItem value={DAY_MODE_NOT_SET} text={t('branchHours.notSet')} />
                <SelectItem value={DAY_MODE_CLOSED} text={t('branchHours.closed')} />
                <SelectItem value={DAY_MODE_OPEN} text={t('branchHours.open')} />
              </Select>

              {mode === DAY_MODE_OPEN && (
                <div className={'branch-hours__windows'}>
                  {windows.map((window, index) => (
                    <div key={index} className={'branch-hours__window'}>
                      <TextInput
                        id={`opening-hours-${day}-${index}-open`}
                        type={'time'}
                        labelText={t('branchHours.from')}
                        value={window.open}
                        onChange={(event) => onChange(setWindow(value, day, index, { open: event.target.value }))}
                      />
                      <TextInput
                        id={`opening-hours-${day}-${index}-close`}
                        type={'time'}
                        labelText={t('branchHours.to')}
                        value={window.close}
                        onChange={(event) => onChange(setWindow(value, day, index, { close: event.target.value }))}
                      />
                      <Button
                        className={'branch-hours__remove'}
                        renderIcon={TrashCan}
                        hasIconOnly
                        kind={'ghost'}
                        size={'sm'}
                        iconDescription={t('branchHours.removeWindow')}
                        onClick={() => onChange(removeWindow(value, day, index))}
                      />
                    </div>
                  ))}
                  <Button
                    className={'branch-hours__add'}
                    renderIcon={Add}
                    kind={'ghost'}
                    size={'sm'}
                    onClick={() => onChange(addWindow(value, day))}
                  >
                    {t('branchHours.addWindow')}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </Stack>
    </fieldset>
  );
});

OpeningHoursField.displayName = 'OpeningHoursField';

export { OpeningHoursField };
export default OpeningHoursField;
